import { createRoot } from "react-dom/client";
import { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
	BrowserRouter,
	Routes,
	Route,
	useSearchParams,
	Outlet,
	Navigate,
	useNavigate,
	useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CourseSettings, InstituteSettings, Lms, PlatformInfo } from "@verity/api-client/types";
import { packageLanguageChanger, VerityInstituteSettings, useMobileLayout, CourseInfo } from "@verity/components";
import { CoreSpinner, CoreToastContainer, ModalContext, ModalContextType } from "@verity/components/core";
import { EventBanner } from "../types/general-types";
import { PostMessageService } from "../helpers/postMessage";
import { getExtensionVersionFromExtension } from "../setup/helpers/extension";
import apiClient, { sendExtensionAuthCode, sendPlatformAuthCodes } from "../helpers/api-client";
import { initVerityLogger } from "../helpers/logger";
import createPortalAuthenticationManager from "./portal-authentication-manager";
import { handleAuthenticatedTabOpen } from "../helpers/open-authenticated";
import { isValidUuidString } from "../helpers/uuid";
import { getBrowserType, isPlatformCompatible } from "../helpers/device";
import { getSubdomain } from "../helpers/url";
import { UserPrivileges } from "../helpers/privilege";
import { error403, getLogoutUrl, PORTAL_TOAST_CONTAINER_ID, useIsAdmin, useIsInstructor } from "./shared";
import { PortalContext, PortalPageKey, portalPages, UserPortalContext } from "./portal-config";
import PortalAuthenticationManagerContext, {
	ApiIntegrationInfo,
	DEFAULT_COURSE_SETTINGS,
	DEFAULT_INSTITUTE_SETTINGS,
	DEFAULT_VERITY_INSTITUTE_SETTINGS,
	defaultHomeOrigin,
	PortalContextUpdate,
	PortalController,
	PortalControllerContext,
	PortalUserInfo,
	PortalUiElement,
} from "./context";
import PortalMenu from "./components/portal-menu";
import PortalTopBar from "./components/portal-top-bar";
import BackButton from "../components/back-button";
import { NpsSurveyModal } from "./components/nps-survey-modal";
import FeedbackButton from "./components/feedback-button";
import EventBanners from "./components/event-banner-handler";
import QuickSetupGuide from "./components/quick-setup-guide";
import { InstitutionReportEmailData } from "../reports/shared/report-types";
import LatestUsageReportModal from "../reports/shared/latest-usage-report-modal";
import styles from "./portal.module.css";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";

type InitialPortalState = Partial<
	Pick<PortalController, "topFrame" | "currPage" | "returnToAssessmentInfo" | "action">
> & {
	reportContext?: { lmsProvidedCourseId?: string; lmsProvidedAssessmentId?: string };
};

type NpsSurveyNotification = {
	type: "nps-survey";
	details: { url: string };
};

type LatestReportNotification = {
	type: "latest-report";
	details: { email: InstitutionReportEmailData };
};

type EventBannerNotification = {
	type: "event-banner";
	details: EventBanner;
};

// create portal authentication manager
const portalAuthenticationManager = createPortalAuthenticationManager();

function isTopFrame() {
	try {
		return window.self === window.top;
	} catch {
		return false;
	}
}

function getInitialState(): InitialPortalState {
	let initialStateJson: Omit<InitialPortalState, "topFrame"> | undefined;
	const initialStateString = localStorage.getItem("initialPortalState");
	if (initialStateString) {
		localStorage.removeItem("initialPortalState");
		try {
			initialStateJson = JSON.parse(initialStateString);
		} catch (e) {
			console.error(e);
		}
	}
	return {
		...initialStateJson,
		topFrame: isTopFrame(),
	};
}

function catchLogAsyncError<T>(promise: Promise<T>): Promise<T | undefined> {
	return promise.catch((e) => {
		console.error(e);
		return undefined;
	});
}

async function getPortalContext(searchParams: URLSearchParams) {
	const context: PortalContext = { homeOrigin: defaultHomeOrigin };

	const lms = searchParams.get("lms");

	let instituteIdParam = searchParams.get("instituteId");
	let institute: { id: number; uuid: string; origin?: string | null; lms?: string | null } | undefined;
	if (!instituteIdParam) {
		const subdomain = getSubdomain();
		if (subdomain) {
			instituteIdParam = (await catchLogAsyncError(apiClient.institute.getAccessControlId({ subdomain }))) ?? null;
		}
	}
	const instituteIdNumber = Number(instituteIdParam);
	if (instituteIdParam) {
		if (!Number.isNaN(instituteIdNumber)) {
			context.instituteId = instituteIdNumber;
			institute = await catchLogAsyncError(apiClient.institute.get(instituteIdNumber));
			context.instituteUuid = institute?.uuid;
		} else if (isValidUuidString(instituteIdParam)) {
			context.instituteUuid = instituteIdParam;
			context.instituteId = await catchLogAsyncError(apiClient.institute.getInstituteIdFromUuid(instituteIdParam));
			if (context.instituteId) {
				institute = await catchLogAsyncError(apiClient.institute.get(context.instituteId));
			}
		}
		context.lms = (institute?.lms || lms || undefined) as Lms | undefined;
		if (context.instituteUuid) {
			const accessControlInstitute = await catchLogAsyncError(
				apiClient.institute.getAccessControlInfo(context.instituteUuid),
			);
			if (accessControlInstitute?.subdomain) {
				context.homeOrigin = `https://${accessControlInstitute.subdomain}.verity.yuja.com`;
			}
		}
		context.instituteIntegrationOrigin = institute?.origin ?? undefined;
		if (context.instituteIntegrationOrigin) {
			try {
				context.instituteIntegrationHostname = new URL(context.instituteIntegrationOrigin).hostname;
			} catch (e) {
				console.error(e);
			}
		}
		context.portalOrigin = context.instituteIntegrationOrigin;
		context.portalHostname = context.instituteIntegrationHostname;
		if (context.instituteUuid) {
			const instituteHostnames = (
				await catchLogAsyncError(apiClient.institute.getHostnames({ instituteUuid: context.instituteUuid }))
			)?.hostnames;

			/**
			 * Try getting top window origin via ancestorOrigins or capabilities postMessage
			 */
			let parentOrigin: string | undefined;
			try {
				if (window.top !== window) {
					const doesAncestorOriginsExist = Object.prototype.hasOwnProperty.call(window.location, "ancestorOrigins");
					if (doesAncestorOriginsExist) {
						parentOrigin = window.location.ancestorOrigins[0];
					} else {
						const postMessageService = new PostMessageService({
							sourceWindow: window,
							targetWindow: window.top ?? window.parent,
							targetOrigin: "*",
							timeout: 5000,
						});
						const response = await postMessageService.fetch({ subject: "lti.capabilities" }, "subject");
						parentOrigin = response.origin;
					}
				}
			} catch (e) {
				console.error("Failed to determine top origin", e);
			}

			if (parentOrigin && parentOrigin !== window.location.origin) {
				const parentHostname = new URL(parentOrigin).hostname;
				const isVanityUrl = instituteHostnames?.includes(parentHostname);
				if (isVanityUrl) {
					context.portalOrigin = parentOrigin;
					context.portalHostname = parentHostname;
				}
			}
		}
	}

	const lmsCourseId = searchParams.get("lmsCourseId");
	if (lmsCourseId) {
		context.lmsProvidedCourseId = lmsCourseId;
		const course = await apiClient.course
			.getCourseInfoFromLmsCourseId({
				lmsCourseId,
				instituteId: context.instituteId,
			})
			.catch(() => undefined);
		context.courseId = course?.id;
		context.courseUuid = course?.accessControlUuid;
		if (context.lms === "BlackboardLearn") {
			context.lmsDetails = { type: course?.isBlackboardUltra ? "ultra" : "original" };
		}
	}

	return context;
}

async function getUserInfo(): Promise<PortalUserInfo> {
	const verityUser = await apiClient.user.getUserInfo().catch(console.error);
	return {
		...verityUser,
		uuid: verityUser?.uuid ?? (await apiClient.getJwt())?.uid,
	};
}

function Portal({
	modalContainer,
	eventBanners,
	loginCount,
}: {
	modalContainer: RefObject<HTMLDivElement>;
	eventBanners: EventBanner[];
	loginCount?: number;
}) {
	const {
		currPage,
		navigateToPage,
		pages,
		portalContext,
		returnToAssessmentInfo,
		openQuickSetupGuide,
		userPlatformInfo,
		dismissableUiElements,
	} = useContext(PortalControllerContext);
	const { lms } = portalContext;
	const { i18n } = useTranslation();
	const userIsInstructor = useIsInstructor();
	const userIsAdmin = useIsAdmin();
	const inMobile = userPlatformInfo.platform === "mobile";
	const isMobileLayout = useMobileLayout();

	useEffect(() => {
		const isFirstLogin = loginCount !== undefined && loginCount <= 1;
		if (isFirstLogin) openQuickSetupGuide();
	}, [loginCount]);

	return (
		<div className={styles.page}>
			<CoreToastContainer enablePortal containerId={PORTAL_TOAST_CONTAINER_ID} />
			{(!inMobile || userIsInstructor) && <PortalTopBar />}
			{!userIsInstructor && !returnToAssessmentInfo && "assessments" in pages && currPage !== "assessments" && (
				<div className={styles.backButtonWrapper}>
					<BackButton onBack={() => navigateToPage("assessments")} />
				</div>
			)}
			<div className={styles.horizontalWrapper}>
				{userIsInstructor && !returnToAssessmentInfo && !isMobileLayout && <PortalMenu />}
				<main
					className={styles.pageContent}
					id="verity-main-content"
					aria-label={i18n.t("portal.portalTSX.mainAriaLabel")}
				>
					<EventBanners eventBanners={eventBanners} />
					{lms !== "BlackboardLearn" && userIsAdmin && dismissableUiElements.has("quickSetupGuide") && (
						<QuickSetupGuide />
					)}
					<Outlet />
				</main>
				<div className={styles.feedbackContainer}>
					<FeedbackButton />
				</div>
			</div>
			<div className={styles.modalContainer} ref={modalContainer} />
		</div>
	);
}

function PortalRoutes({ initialState = {} }: { initialState?: InitialPortalState }) {
	const { topFrame, returnToAssessmentInfo, action } = initialState;
	const { i18n } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const location = useLocation();
	const navigate = useNavigate();

	const [portalContext, setPortalContext] = useState<PortalContext>({
		homeOrigin: defaultHomeOrigin,
	});
	const [userPortalContext, setUserPortalContext] =
		useState<Pick<UserPortalContext, "courseId" | "courseUuid" | "lmsProvidedCourseId" | "assessmentId">>(
			portalContext,
		);
	const [isLoading, setLoading] = useState(true);
	const [userInfo, setUserInfo] = useState<PortalUserInfo | undefined>();
	const [authenticated, setAuthenticated] = useState(false);
	const [privileges, setPrivileges] = useState<UserPrivileges>({});
	const [apiIntegrations, setApiIntegrations] = useState<ApiIntegrationInfo>({});
	const [platformInfo, setPlatformInfo] = useState<PlatformInfo>({
		platform: "none",
		versionSupported: false,
	});
	const browserType = getBrowserType(platformInfo["platform"]);
	const [verityInstituteSettings, setVerityInstituteSettings] = useState<VerityInstituteSettings>(
		DEFAULT_VERITY_INSTITUTE_SETTINGS,
	);
	const [instituteSettings, setInstituteSettings] = useState<InstituteSettings>(DEFAULT_INSTITUTE_SETTINGS);
	const [canSwitchCourse, setCanSwitchCourse] = useState(false);
	const [courseInfo, setCourseInfo] = useState<CourseInfo | undefined>(undefined);
	const [courseSettings, setCourseSettings] = useState<CourseSettings>(DEFAULT_COURSE_SETTINGS);
	const [initialAction, setInitialAction] = useState(action);
	const [visualizerDetected, setVisualizerDetected] = useState<boolean | undefined>(undefined);
	const [loginCount, setLoginCount] = useState<number | undefined>(undefined);
	const [dismissedItems, setDismissedItems] = useState<Set<PortalUiElement>>(
		new Set(["visualizerBanner", "quickSetupGuide"]),
	);

	const [npsSurveyUrl, setNpsSurveyUrl] = useState<string | undefined>(undefined);
	const [latestUsageReportEmail, setLatestUsageReportEmail] = useState<InstitutionReportEmailData | undefined>(
		undefined,
	);
	const [eventBanners, setEventBanners] = useState<EventBanner[]>([]);

	const modalContainer = useRef<HTMLDivElement>(null);

	const createNavigateParams = useCallback(
		(pageKey: string) => ({
			pathname: `/${pageKey}`,
			search: searchParams.toString(),
		}),
		[searchParams],
	);

	const extractPageKey = (pathName: string): PortalPageKey | undefined => {
		const result = /^\/(\w+)$/.exec(pathName);
		if (result?.[1] && result[1] in portalPages) {
			return result[1] as PortalPageKey;
		}
		return undefined;
	};

	const currPage = extractPageKey(location.pathname);

	useEffect(() => {
		void apiClient.authentication
			.isAuthenticated()
			.then(async (isAuth: boolean) => {
				if (isAuth) {
					const instituteLanguage = await apiClient.institute.getLanguage();
					const userLanguage = await apiClient.user.getUserLanguage(await apiClient.user.getUserId());
					const displayedLanguage = userLanguage ?? instituteLanguage ?? "en";
					await i18n.changeLanguage(displayedLanguage);
					await packageLanguageChanger(displayedLanguage);
					if (window.chrome) {
						chrome?.runtime?.sendMessage?.(
							import.meta.env.VITE_EXTENSION_ID,
							{
								request: "changeLanguage",
								language: displayedLanguage,
							},
							() => {
								if (chrome?.runtime?.lastError) {
									console.error(`'Error changing language in extension': ${chrome.runtime.lastError.message}`);
								}
							},
						);
					} else {
						dispatchProxyMessage({
							request: "changeLanguage",
							language: displayedLanguage,
						}).catch((error) => {
							console.error("Error changing language:", error);
						});
					}
					window.top?.postMessage(
						{
							subject: "__LANGUAGE_CHANGE",
							payload: { language: displayedLanguage },
						},
						"*",
					);
				} else {
					await i18n.changeLanguage("en");
					await packageLanguageChanger("en");
				}
			})
			.catch(async (error) => {
				console.error("Error getting language from storage:", error);
				await i18n.changeLanguage("en");
				await packageLanguageChanger("en");
			});
	}, []);

	const getNotifications = async (instituteUuid: string, courseUuid?: string) => {
		const notificationsResp = await apiClient.user.getNotifications({ instituteUuid, courseUuid });

		const npsSurveyNotification = notificationsResp.find((n): n is NpsSurveyNotification => n.type === "nps-survey");

		const latestReportNotification = notificationsResp.find(
			(n): n is LatestReportNotification => n.type === "latest-report",
		);

		const eventBannerNotifications = notificationsResp.filter(
			(n): n is EventBannerNotification => n.type === "event-banner",
		);

		if (npsSurveyNotification) {
			setNpsSurveyUrl(npsSurveyNotification.details.url);
			apiClient.user.dismissNotifications({ type: "nps-survey" }).catch(console.error);
		}

		if (latestReportNotification) {
			setLatestUsageReportEmail(latestReportNotification.details.email);
			apiClient.user.dismissNotifications({ type: "latest-report" }).catch(console.error);
		}

		if (eventBannerNotifications.length > 0) {
			const banners = eventBannerNotifications.map((n) => n.details);
			setEventBanners(banners);
		}
	};

	const updatePortalContext = useCallback(
		async ({ courseId, assessmentId }: PortalContextUpdate) => {
			const newContext = { ...userPortalContext };
			if (courseId !== undefined) {
				newContext.courseId = courseId ?? undefined;
			}
			if (assessmentId !== undefined) {
				newContext.assessmentId = assessmentId ?? undefined;
			}
			if (newContext.courseId !== userPortalContext.courseId) {
				if (newContext.courseId) {
					searchParams.set("course", String(newContext.courseId));
					try {
						const course = await apiClient.course.getCourseInfoFromId(newContext.courseId);
						newContext.courseUuid = course.accessControlUuid;
						newContext.lmsProvidedCourseId = course.courseId;
					} catch (e) {
						console.error(e);
					}
				} else {
					newContext.courseUuid = undefined;
					newContext.lmsProvidedCourseId = undefined;
					searchParams.delete("course");
				}
			}
			if (newContext.assessmentId !== userPortalContext.assessmentId) {
				if (newContext.assessmentId) {
					searchParams.set("quizId", String(newContext.assessmentId));
				} else {
					searchParams.delete("quizId");
				}
			}
			setUserPortalContext(newContext);
			setSearchParams(searchParams, { replace: true });
		},
		[searchParams, setSearchParams, userPortalContext],
	);

	useEffect(() => {
		(async () => {
			searchParams.delete("redirect");
			searchParams.delete("refreshToken");
			searchParams.delete("extRefreshToken");
			searchParams.delete("accountId");
			setSearchParams(searchParams, { replace: true });
			try {
				await apiClient.updateTokens();
				void apiClient.user
					.updateLoginCount()
					.then((updatedLoginCount) => setLoginCount(updatedLoginCount.loginCount))
					.catch((e) => console.error(e));
				if (topFrame) {
					void sendPlatformAuthCodes();
				}
			} catch (e) {
				console.error(e);
			}
			try {
				const context = await getPortalContext(searchParams);
				setPortalContext(context);
				const shouldCheckVisualizer = context.instituteId && context.lms && context.lms !== "BlackboardLearn";
				if (context.instituteId) {
					searchParams.set("instituteId", String(context.instituteId));
					setSearchParams(searchParams, { replace: true });
				}
				if (context.instituteUuid) {
					getNotifications(context.instituteUuid, context.courseUuid);
				}
				const [
					userInfoResp,
					authenticatedResp,
					privilegesResp,
					apiIntegrationsResp,
					platformInfoResp,
					verityInstituteSettingsResp,
					instituteSettingsResp,
					courseInfoResp,
					courseSettingsResp,
					reportCourseResp,
					reportAssessmentResp,
					visualizerStatusResp,
				] = await Promise.all([
					getUserInfo().catch(console.error),
					apiClient.authentication.isAuthenticated().catch((e) => {
						console.error(e);
						return false;
					}),
					apiClient.permission.getUserPrivileges().catch(console.error),
					context.courseId == null || context.instituteId == null
						? undefined
						: apiClient.lmsInfo
								.getApiIntegrationInfo({ courseId: context.courseId, instituteId: context.instituteId })
								.catch(console.error),
					apiClient.platformInfo.current().catch(console.error),
					apiClient.institute.getVeritySettings().catch(console.error),
					context.instituteUuid
						? apiClient.institute.getSettings(context.instituteUuid).catch(console.error)
						: undefined,
					context.courseId ? apiClient.course.getCourseInfoFromId(context.courseId).catch(console.error) : undefined,
					context.courseId ? apiClient.course.getCourseSettings(context.courseId).catch(console.error) : undefined,
					initialState.reportContext?.lmsProvidedCourseId
						? apiClient.course
								.getCourseInfoFromLmsCourseId({
									lmsCourseId: initialState.reportContext.lmsProvidedCourseId,
									instituteId: context.instituteId,
								})
								.catch(console.error)
						: undefined,
					initialState.reportContext?.lmsProvidedAssessmentId
						? apiClient.assessment
								.getAssessmentByAssessmentInfo({
									courseId: initialState.reportContext?.lmsProvidedCourseId ?? context.lmsProvidedCourseId,
									quizId: initialState.reportContext.lmsProvidedAssessmentId,
								})
								.catch(console.error)
						: undefined,
					shouldCheckVisualizer && context.instituteId
						? apiClient.institute.getVisualizerStatus(context.instituteId).catch(console.error)
						: undefined,
				]);
				if (userInfoResp) {
					setUserInfo(userInfoResp);
				}
				setAuthenticated(authenticatedResp);
				if (privilegesResp) {
					setPrivileges(privilegesResp);
				}
				if (apiIntegrationsResp) {
					setApiIntegrations({
						course: apiIntegrationsResp?.course ?? undefined,
						institute: apiIntegrationsResp?.institute ?? undefined,
					});
				}
				if (platformInfoResp) {
					setPlatformInfo(platformInfoResp);
				}
				if (instituteSettingsResp) {
					setInstituteSettings(instituteSettingsResp);
				}
				if (verityInstituteSettingsResp) {
					setVerityInstituteSettings(verityInstituteSettingsResp);
				}
				if (courseInfoResp) {
					setCourseInfo({ name: courseInfoResp.name ?? undefined, code: courseInfoResp.code ?? undefined });
				}
				if (courseSettingsResp) {
					setCourseSettings(courseSettingsResp);
				}
				if (shouldCheckVisualizer && visualizerStatusResp) {
					setVisualizerDetected(visualizerStatusResp.active);
					!visualizerStatusResp.active
						? showDismissableUiElement("visualizerBanner")
						: dismissUiElement("visualizerBanner");
				} else {
					setVisualizerDetected(false);
					dismissUiElement("visualizerBanner");
				}

				const reportCourseId = reportAssessmentResp?.courseId ?? reportCourseResp?.id;
				const reportAssessmentId = reportAssessmentResp?.id;
				let initialPortalContextUpdate: PortalContextUpdate = {
					courseId: reportCourseId,
					assessmentId: reportAssessmentId,
				};
				if (!initialPortalContextUpdate.courseId) {
					const courseIdString = searchParams.get("course") ?? undefined;
					const courseId = courseIdString ? Number(courseIdString) : undefined;
					initialPortalContextUpdate.courseId = !courseId || isNaN(courseId) ? undefined : courseId;
					if (!initialPortalContextUpdate.assessmentId) {
						const assessmentIdString = searchParams.get("quizId") ?? undefined;
						const assessmentId = assessmentIdString ? Number(assessmentIdString) : undefined;
						initialPortalContextUpdate.assessmentId = !assessmentId || isNaN(assessmentId) ? undefined : assessmentId;
					}
				}

				if (context.instituteId && !context.courseId) {
					// select first course if no initial course (if possible)
					const allCourses = await apiClient.course.getInstituteCoursesInfo(context.instituteId);
					if (!initialPortalContextUpdate.courseId && allCourses.length > 0) {
						const [firstCourse] = allCourses;
						initialPortalContextUpdate = {
							courseId: firstCourse.id,
							assessmentId: null,
						};
					}
					setCanSwitchCourse(allCourses.length > 1);
				}
				if (initialPortalContextUpdate.courseId || initialPortalContextUpdate.assessmentId) {
					await updatePortalContext(initialPortalContextUpdate);
				}
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	// Not used anymore, instead check getVisualizerStatus for the institute
	// const checkThemeExists = (lms: Lms) => {
	// 	const targetWindow = window.top ?? window.parent;

	// 	if (topFrame || lms === "BlackboardLearn" || !targetWindow) {
	// 		return;
	// 	}

	// 	const onMessage = (event: MessageEvent) => {
	// 		if (event.data?.subject === ThemeMessageSubject.themeExists) {
	// 			setShowThemeBanner(false);
	// 			clearTimeout(timeoutId);
	// 			window.removeEventListener("message", onMessage);
	// 		}
	// 	};

	// 	const timeoutId = setTimeout(() => {
	// 		setShowThemeBanner(true);
	// 		window.removeEventListener("message", onMessage);
	// 	}, 5000);

	// 	window.addEventListener("message", onMessage);

	// 	targetWindow.postMessage(
	// 		{
	// 			subject: ThemeMessageSubject.ltiLoaded,
	// 			lms: lms,
	// 		},
	// 		"*",
	// 	);
	// };

	useEffect(() => {
		if (isPlatformCompatible("extension", browserType)) {
			const interval = setInterval(async () => {
				const newExtensionVersion = await getExtensionVersionFromExtension(import.meta.env.VITE_EXTENSION_ID);
				if (
					(platformInfo.platform === "extension" &&
						(!newExtensionVersion || (platformInfo.version && newExtensionVersion !== platformInfo.version))) ||
					(platformInfo.platform === "none" && newExtensionVersion)
				) {
					if (platformInfo.platform === "extension" && !newExtensionVersion) {
						setPlatformInfo({ platform: "none", versionSupported: false });
					}
					const updatedPlatform = await apiClient.platformInfo.current();
					setPlatformInfo(updatedPlatform);
					if (updatedPlatform.platform === "extension") {
						await sendExtensionAuthCode();
					}
				}
			}, 5000);
			return () => clearInterval(interval);
		}
	}, [browserType, platformInfo]);

	// cannot change the main context's course if it was already provided (eg., by LTI page)
	const mergedPortalContext = useMemo(() => {
		const mergedContext: PortalContext & UserPortalContext = {
			...portalContext,
			assessmentId: userPortalContext.assessmentId,
		};
		if (!mergedContext.courseId) {
			mergedContext.courseId = userPortalContext.courseId;
			mergedContext.courseUuid = userPortalContext.courseUuid;
			mergedContext.lmsProvidedCourseId = userPortalContext.lmsProvidedCourseId;
		}
		return mergedContext;
	}, [portalContext, userPortalContext]);

	useEffect(() => {
		(async () => {
			if (portalContext.instituteId && mergedPortalContext.courseId) {
				const [courseInfoResp, apiIntegrationsResp, courseSettingsResp] = await Promise.all([
					apiClient.course.getCourseInfoFromId(mergedPortalContext.courseId).catch(console.error),
					apiClient.lmsInfo
						.getApiIntegrationInfo({
							instituteId: portalContext.instituteId,
							courseId: mergedPortalContext.courseId,
						})
						.catch(console.error),
					apiClient.course.getCourseSettings(mergedPortalContext.courseId).catch(console.error),
				]);
				if (courseInfoResp) {
					setCourseInfo({ name: courseInfoResp.name ?? undefined, code: courseInfoResp.code ?? undefined });
				} else {
					setCourseInfo({});
				}
				if (apiIntegrationsResp) {
					setApiIntegrations((currApiIntegrations) => ({
						...currApiIntegrations,
						course: apiIntegrationsResp.course ?? undefined,
					}));
				} else {
					setApiIntegrations((currApiIntegrations) => ({ institute: currApiIntegrations.institute }));
				}
				if (courseSettingsResp) {
					setCourseSettings(courseSettingsResp);
				} else {
					setCourseSettings(DEFAULT_COURSE_SETTINGS);
				}
			} else {
				setCourseInfo({});
				setApiIntegrations((currApiIntegrations) => ({ institute: currApiIntegrations.institute }));
				setCourseSettings(DEFAULT_COURSE_SETTINGS);
			}
		})();
	}, [mergedPortalContext.courseId]);

	const filteredPages = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(portalPages).filter(
					([, page]) =>
						!page.isAllowed ||
						page.isAllowed({
							...mergedPortalContext,
							privileges,
							platformInfo,
							browserType,
							verityInstituteSettings,
						}),
				),
			),
		[mergedPortalContext, privileges, platformInfo, verityInstituteSettings],
	);

	useEffect(() => {
		if (!isLoading) {
			if (!authenticated && topFrame) {
				window.location.href = getLogoutUrl(portalContext.homeOrigin);
			} else if (initialState.currPage && initialState.currPage in filteredPages) {
				navigate(createNavigateParams(initialState.currPage), { replace: true });
			}
		}
	}, [isLoading]);

	const showDismissableUiElement = useCallback((item: PortalUiElement) => {
		setDismissedItems((prev) => {
			const newSet = new Set(prev);
			newSet.delete(item);
			return newSet;
		});
	}, []);

	const dismissUiElement = useCallback((item: PortalUiElement) => {
		setDismissedItems((prev) => {
			const newSet = new Set(prev);
			newSet.add(item);
			return newSet;
		});
	}, []);

	const portalController: PortalController = useMemo(() => {
		const dismissableUiElements = new Set<PortalUiElement>();
		if (portalContext?.lms) {
			if (loginCount !== undefined && loginCount <= 1 && !dismissedItems.has("introVideo")) {
				dismissableUiElements.add("introVideo");
			}
			if (!dismissedItems.has("visualizerBanner")) {
				dismissableUiElements.add("visualizerBanner");
			}
			if (!dismissedItems.has("quickSetupGuide")) {
				dismissableUiElements.add("quickSetupGuide");
			}
		}

		return {
			topFrame: topFrame ?? false,
			initialPortalContext: portalContext,
			userPlatformInfo: platformInfo,
			browserType,
			verityInstituteSettings,
			instituteSettings,
			courseInfo,
			courseSettings,
			userInfo,
			userPrivileges: privileges,
			apiIntegrations,
			fetchApiIntegrations: async () => {
				if (portalContext.courseId && portalContext.instituteId) {
					const apiIntegrations = await apiClient.lmsInfo
						.getApiIntegrationInfo({
							courseId: portalContext.courseId,
							instituteId: portalContext.instituteId,
						})
						.catch(console.error);
					if (apiIntegrations) {
						setApiIntegrations({
							course: apiIntegrations.course ?? undefined,
							institute: apiIntegrations.institute ?? undefined,
						});
						return;
					}
				}
				setApiIntegrations({});
			},
			pages: filteredPages,
			currPage,
			action: initialAction,
			setAction: setInitialAction,
			navigateToPage(pageKey, state) {
				navigate(createNavigateParams(pageKey), { state, replace: !topFrame });
			},
			reportContext: { ...portalContext, ...userPortalContext },
			portalContext: mergedPortalContext,
			changePortalContext: updatePortalContext,
			returnToAssessmentInfo,
			openQuickSetupGuide: (shouldNavigate?: boolean) => {
				showDismissableUiElement("quickSetupGuide");
				if (shouldNavigate) {
					const isMoodle = portalContext?.lms === "moodle";
					const hasMoodleApiToken = isMoodle && apiIntegrations?.institute?.edit;
					const visualizerSelected =
						currPage === "integrationSettings" && location.state?.selected === "LMS Visualizer";
					const apiSelected = currPage === "integrationSettings" && location.state?.selected === "LMS API";
					const onValidPage = visualizerSelected || (isMoodle && apiSelected);
					if (!onValidPage) {
						const goToApiPage = isMoodle && visualizerDetected && !hasMoodleApiToken;
						navigate(createNavigateParams("integrationSettings"), {
							state: { selected: goToApiPage ? "LMS API" : "LMS Visualizer" },
							replace: !topFrame,
						});
					}
				}
			},
			visualizerDetected,
			canSwitchCourse,
			loginCount,
			dismissableUiElements,
			dismissUiElement,
		};
	}, [
		portalContext,
		platformInfo,
		browserType,
		userInfo,
		privileges,
		apiIntegrations,
		verityInstituteSettings,
		instituteSettings,
		courseInfo,
		courseSettings,
		filteredPages,
		currPage,
		createNavigateParams,
		userPortalContext,
		mergedPortalContext,
		updatePortalContext,
		returnToAssessmentInfo,
		initialAction,
		topFrame,
		visualizerDetected,
		location,
		canSwitchCourse,
		loginCount,
		dismissedItems,
		showDismissableUiElement,
		dismissUiElement,
	]);

	const modalContext: ModalContextType = useMemo(() => ({ modalContainer }), [modalContainer]);

	if (isLoading) {
		return (
			<div className={styles.spinnerWrapper}>
				<CoreSpinner size={100} />
			</div>
		);
	}

	if (!authenticated) {
		return <iframe className={styles.iframePage} src={`${error403}?browser=true`} />;
	}

	const defaultPage = Object.values(filteredPages).find(({ isDefault }) =>
		isDefault?.({
			...portalContext,
			platformInfo,
			browserType,
			privileges,
			allowedPages: new Set(Object.keys(filteredPages) as PortalPageKey[]),
		}),
	);

	return (
		<PortalControllerContext.Provider value={portalController}>
			<ModalContext.Provider value={modalContext}>
				<Routes>
					<Route
						element={<Portal modalContainer={modalContainer} eventBanners={eventBanners} loginCount={loginCount} />}
					>
						{Object.values(filteredPages).map((page) => (
							<Route
								key={page.key}
								path={`/${page.key}`}
								element={
									page.component ? (
										page.component(mergedPortalContext)
									) : (
										<iframe className={styles.iframePage} src={page.link(mergedPortalContext)} />
									)
								}
							/>
						))}
						<Route // not found
							path="*"
							element={
								<Navigate replace to={createNavigateParams(defaultPage?.key ?? Object.keys(filteredPages)[0])} />
							}
						/>
					</Route>
				</Routes>
				{npsSurveyUrl && <NpsSurveyModal onClose={() => setNpsSurveyUrl(undefined)} url={npsSurveyUrl} />}
				{latestUsageReportEmail && (
					<LatestUsageReportModal
						open={true}
						email={latestUsageReportEmail}
						onClose={() => {
							setLatestUsageReportEmail(undefined);
						}}
					/>
				)}
			</ModalContext.Provider>
		</PortalControllerContext.Provider>
	);
}

function PortalRouter({ initialState = {} }: { initialState?: InitialPortalState }) {
	return (
		<BrowserRouter basename="/portal.html">
			<PortalRoutes initialState={initialState} />
		</BrowserRouter>
	);
}

const container = document.getElementById("verity-portal-target");
const root = createRoot(container!);
const initialState: InitialPortalState = getInitialState();
handleAuthenticatedTabOpen()
	.then(() => portalAuthenticationManager.init())
	.then(() =>
		root.render(
			<PortalAuthenticationManagerContext.Provider value={portalAuthenticationManager}>
				<PortalRouter initialState={initialState} />
			</PortalAuthenticationManagerContext.Provider>,
		),
	)
	.catch(() => root.render(<PortalRouter initialState={initialState} />));
initVerityLogger();
